import { FC, useState, useMemo } from "react";
import { Star, StarOff, Trash2, GripVertical } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import { Search } from "@/models/search/Search";
import useSettingsStore from "@/stores/settings.store";
import CopyLinkButton from "@/pages/global-search/components/search-input/CopyLinkButton";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import { getTabsName } from "@/pages/global-search/utils/searchUtils";
import { getFilterDisplayName } from "@/pages/global-search/utils/entityInfoHelpers";
import { useGlobalSearchContext } from "@/providers/global-search/GlobalSearchContext";
import { useOpenStoredSearch } from "@/hooks/useOpenStoredSearch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@capacit-com/lara-law-shared-ui";
import type { DragHandleProps } from "./SortableFavoriteSearchGrid";

type SearchColor = { colorType: string; color: string };

interface RecentSearchCardProps {
  search: Search;
  colors: SearchColor[];
  favoriteSearch: (searchId: string) => void;
  unfavoriteSearch: (searchId: string) => void;
  deleteSearch: (searchId: string) => void;
  isDeletingPending: (searchId: string) => boolean;
  isAnyFavoritePending: (searchId: string) => boolean;
  dragHandleProps?: DragHandleProps | null;
}

const RecentSearchCard: FC<RecentSearchCardProps> = ({
  search,
  colors,
  favoriteSearch,
  unfavoriteSearch,
  deleteSearch,
  isDeletingPending,
  isAnyFavoritePending,
  dragHandleProps,
}) => {
  const [favoriteHovered, setFavoriteHovered] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { isDarkMode } = useSettingsStore();
  const isFavorite = search.isFavorite || search.favoriteLinkId !== null;
  const isFavoriteBusy = isAnyFavoritePending(search.id!);
  const isDeleteBusy = isDeletingPending(search.id!);
  const { leveledFilters } = useGlobalSearchContext();
  const openStoredSearch = useOpenStoredSearch();

  const getActiveFilter = (search: Search) => {
    if (!search.filters || Object.keys(search.filters).length === 0)
      return null;

    // Return all filters (filters are now flat, no nested structure)
    return Object.entries(search.filters).map(([key, values]) => ({
      key,
      values: Array.isArray(values) ? values : [values],
    }));
  };

  const activeFilter = getActiveFilter(search);

  const formatFilterKey = (key: string): string => {
    if (key === "datasource") return "Kilde";
    return getFilterDisplayName(key, leveledFilters);
  };

  const filterColorMap: Record<string, { bg: string; text: string }> = {
    tab: {
      bg: "bg-muted",
      text: "text-muted-foreground",
    },
    source: {
      bg: "bg-success/20",
      text: "text-success-foreground",
    },
    // Specific colors for different documentType contexts
    workspacedocumenttype: {
      bg: "bg-primary/20",
      text: "text-primary-foreground",
    },
    legislationdocumenttype: {
      bg: "bg-destructive/20",
      text: "text-destructive-foreground",
    },
  };

  // Memoize color calculations to avoid recalculating on every render
  const tabColors = useMemo(() => {
    const lowerKey = "tabs";
    const fallback = filterColorMap[lowerKey] || {
      bg: "bg-muted",
      text: "text-muted-foreground",
    };

    const bgLight = colors.find(
      (c) => c.colorType.toLowerCase() === `${lowerKey}light`
    );
    const bgDark = colors.find(
      (c) => c.colorType.toLowerCase() === `${lowerKey}dark`
    );
    const textLight = colors.find(
      (c) => c.colorType.toLowerCase() === `${lowerKey}textlight`
    );
    const textDark = colors.find(
      (c) => c.colorType.toLowerCase() === `${lowerKey}textdark`
    );

    // If we have custom colors, use inline styles
    if (bgLight && bgDark && textLight && textDark) {
      return {
        bg: "",
        text: "",
        style: {
          backgroundColor: isDarkMode ? bgDark.color : bgLight.color,
          color: isDarkMode ? textDark.color : textLight.color,
        },
      };
    }

    // Otherwise use fallback Tailwind classes
    return { bg: fallback.bg, text: fallback.text };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors, isDarkMode]);

  const getFilterColors = (filterKey: string) => {
    // Create a more specific key that includes the tab context for documentType
    let specificKey = filterKey.toLowerCase();
    if (filterKey.toLowerCase() === "documenttype") {
      // Differentiate between workspace and legislation documentType
      if (search.tab.toLowerCase() === "workspacedocuments") {
        specificKey = "workspacedocumenttype";
      } else if (search.tab.toLowerCase() === "legislation") {
        specificKey = "legislationdocumenttype";
      }
    }

    const fallback = filterColorMap[specificKey] ||
      filterColorMap[filterKey.toLowerCase()] || {
        bg: "bg-muted",
        text: "text-muted-foreground",
      };

    const bgLight = colors.find(
      (c) => c.colorType.toLowerCase() === `${specificKey}light`
    );
    const bgDark = colors.find(
      (c) => c.colorType.toLowerCase() === `${specificKey}dark`
    );
    const textLight = colors.find(
      (c) => c.colorType.toLowerCase() === `${specificKey}textlight`
    );
    const textDark = colors.find(
      (c) => c.colorType.toLowerCase() === `${specificKey}textdark`
    );

    // If we have custom colors, use inline styles
    if (bgLight && bgDark && textLight && textDark) {
      return {
        bg: "",
        text: "",
        style: {
          backgroundColor: isDarkMode ? bgDark.color : bgLight.color,
          color: isDarkMode ? textDark.color : textLight.color,
        },
      };
    }

    // Otherwise use fallback Tailwind classes
    return { bg: fallback.bg, text: fallback.text };
  };

  const handleFavorite = () => {
    if (isFavorite) {
      unfavoriteSearch(search.id!);
    } else {
      favoriteSearch(search.id!);
    }
  };

  const handleDelete = () => {
    if (isFavorite) {
      setIsOpen(true);
    } else {
      deleteSearch(search.id!);
    }
  };

  const formatSearchDate = (dateString?: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Lige nu";
    if (diffMins < 60) return `${diffMins} min siden`;
    if (diffHours < 24)
      return `${diffHours} time${diffHours > 1 ? "r" : ""} siden`;
    if (diffDays < 7) return `${diffDays} dag${diffDays > 1 ? "e" : ""} siden`;

    return date.toLocaleDateString("da-DK", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <>
      <Card
        className="bg-background shadow-xs rounded-md cursor-pointer hover:shadow-sm p-0 gap-0 min-w-0 overflow-hidden"
        onClick={() => openStoredSearch(search)}
      >
        <CardHeader className="px-2 pt-2 pb-1 min-w-0">
          <CardTitle className="text-xs font-normal mb-0 min-w-0">
            <div className="flex flex-row items-center justify-between gap-1 min-w-0">
              <div className="flex min-w-0 flex-1 flex-row items-center gap-1 overflow-hidden">
                {search.searchTerm === "" &&
                (!search.searchTags || search.searchTags.length === 0) ? (
                  <span className="truncate min-w-0">
                    Søgning uden søgeterm
                  </span>
                ) : (
                  <>
                    {search.searchTags && search.searchTags.length > 0 && (
                      <div className="flex min-w-0 flex-row items-center gap-1 overflow-hidden">
                        {search.searchTags.map((tag, index) => (
                          <span
                            key={`tag-${index}`}
                            className="max-w-full shrink truncate rounded-sm bg-primary px-1.5 py-0.5 text-[10px] text-primary-foreground"
                            title={tag}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {search.searchTerm && (
                      <span
                        className="min-w-0 flex-1 truncate"
                        title={search.searchTerm}
                      >
                        {search.searchTerm}
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="flex shrink-0 flex-row gap-1">
                <PermissionGate allOf={[Permission.CreateSearchShare]}>
                  <CopyLinkButton
                    searchQuery={search}
                    buttonClassName="p-0 size-5 hover:bg-muted group"
                    svgSize={3}
                    toolTipLocation="top"
                  />
                </PermissionGate>
                <Button
                  variant="ghost"
                  size="icon"
                  className="p-0 size-5 group hover:bg-muted hover:text-accent-foreground disabled:opacity-100"
                  onMouseEnter={() => setFavoriteHovered(search.id ?? null)}
                  onMouseLeave={() => setFavoriteHovered(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFavorite();
                  }}
                  disabled={isFavoriteBusy}
                >
                  {isFavoriteBusy ? (
                    <Spinner className="w-3 h-3" />
                  ) : isFavorite && favoriteHovered === search.id ? (
                    <StarOff className="size-3 fill-warning" />
                  ) : isFavorite ? (
                    <Star className="size-3 fill-warning stroke-warning" />
                  ) : (
                    <Star className="size-3 group-hover:fill-warning" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="p-0 size-5 group hover:bg-muted hover:text-destructive hover:fill-destructive disabled:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  disabled={isDeleteBusy}
                >
                  {isDeleteBusy ? (
                    <Spinner className="w-3 h-3" />
                  ) : (
                    <Trash2 className="size-3 group-hover:text-destructive" />
                  )}
                </Button>
                {dragHandleProps && (
                  <button
                    type="button"
                    ref={dragHandleProps.setActivatorNodeRef}
                    aria-label="Flyt favorit søgning"
                    className="inline-flex size-5 shrink-0 items-center justify-center rounded-md text-muted-foreground cursor-grab active:cursor-grabbing hover:bg-muted"
                    onClick={(e) => e.stopPropagation()}
                    {...dragHandleProps.attributes}
                    {...dragHandleProps.listeners}
                  >
                    <GripVertical className="size-3.5" />
                  </button>
                )}
              </div>
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent className="px-2 pb-2 pt-0">
          <div className="flex flex-row gap-2 text-[10px] overflow-x-auto items-center">
            <div
              className={`${tabColors.bg} ${tabColors.text} rounded-md px-2 py-1 cursor-pointer whitespace-nowrap`}
              style={tabColors.style}
              title={`Tab: ${getTabsName(search.tab)}`}
            >
              {getTabsName(search.tab)}
            </div>
            {activeFilter &&
              activeFilter.map((filter, index) => {
                const filterColors = getFilterColors(filter.key);
                return (
                  <div
                    key={index}
                    className={`${filterColors.bg} ${filterColors.text} rounded-md px-2 py-1 cursor-pointer whitespace-nowrap`}
                    style={filterColors.style}
                    title={filter.values.join(", ")}
                  >
                    {formatFilterKey(filter.key)}: {filter.values.length}
                  </div>
                );
              })}
            {search.searchedAt && (
              <p className="text-[10px] text-muted-foreground ml-auto">
                {formatSearchDate(search.searchedAt)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <ConfirmationDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Slet søgning"
        description="Er du sikker på, at du vil slette denne søgning?"
        confirmText="Slet"
        onConfirm={() => {
          deleteSearch(search.id!);
          setIsOpen(false);
        }}
        confirmPending={isDeletingPending(search.id!)}
        variant="destructive"
      />
    </>
  );
};

export default RecentSearchCard;
